/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useGlobalSearch, useProcess } from "@/global-data";
import { useDeletePopup } from "@/global-data/use-delete-popup";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useToast } from "@/hooks/use-toast";
import { getUserInfo } from "@/lib/token";
import { wait } from "@/lib/wait";
import { apiServices } from "@/services/api.services";
import { email_listener } from "@/services/global.services";
import { useEffect, useMemo, useState } from "react";

export const useMutations = () => {
  const { toast } = useToast();

  const API_SERVICE = email_listener;
  const { company_id, user_id } = useMemo(() => {
    const tempUserData = getUserInfo();
    return {
      company_id: tempUserData?.company_id,
      user_id: tempUserData?.user_id || "",
    };
  }, []);
  const { setProcess } = useProcess();
  const [skeltonLoading, setSkeltonLoading] = useState(true);
  const [isFormReset, setIsFormReset] = useState(false);
  const [listData, setListData] = useState([]);
  const [filter, setFilter] = useState({});
  const [formPopupOpen, setFormPopupOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(undefined);
  const [activeRecordData, setActiveRecordData] = useState<any>(undefined);
  const [tableColumn, setTableColumn] = useState([]);
  const [passKeyPopup, setPassKeyPopup] = useState(false);
  const [emailProvider, setEmailProvider] = useState(undefined);
  const [aksPopup, setAksPopup] = useState(false);

  const { setSearchProgress, search } = useGlobalSearch();
  const [searchDebounced] = useDebouncedValue(search, 200);

  const { onDelete, status, identity, clearDelete } = useDeletePopup();

  const [paginationData, setPaginationData] = useState({
    page: 1,
    perPage: 10,
    totalItem: 1,
  });

  const [errorMessage, setErrorMessage] = useState<any>({
    title: "",
    message: "",
    open: false,
  });

  const closeErrorMessage = () => {
    setErrorMessage({
      title: "Error",
      message: "",
      open: false,
    });
  };

  //On Load Component
  useEffect(() => {
    (async () => {
      await loadingList({ searchText: "" });
      await wait(1000);
      setSkeltonLoading(false);
      setProcess(false);
    })();
    return () => {
      closeErrorMessage();
    };
  }, []);

  //handle delete
  useEffect(() => {
    if (status === "cancel") {
      setDeleteId(undefined);
      clearDelete();
    }
    if (status === "delete" && identity === "user") {
      (async () => {
        setProcess(true);
        const deleteResponse = await deleteRecord();
        if (deleteResponse.data) {
          await loadingList({});
          toast({
            title: "Success",
            variant: "success",
            description:
              deleteResponse?.data.result.metadata.message ??
              deleteResponse?.data?.status?.msg ??
              deleteResponse?.data?.status?.message,
          });
        }
        clearDelete();
        setDeleteId(undefined);
        setProcess(false);
      })();
    }
  }, [status]);

  const deleteRecord = async () => {
    const payload = {
      data: {
        email_id: deleteId,
        company_id: company_id,
      },
      company_id: company_id,
    };
    const { data, error, message } = await apiServices<any>(
      API_SERVICE.delete,
      "post",
      payload,
    );

    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }
    return { data, error };
  };

  //Handle Search
  useEffect(() => {
    if (skeltonLoading === false) {
      (async () => {
        setSearchProgress(true);
        await loadingList({});
        setSearchProgress(false);
      })();
    }
  }, [searchDebounced]);

  const onFormOpenChange = (prop: boolean) => {
    setFormPopupOpen(prop);
    if (!prop) {
      setEmailProvider(undefined);
    }
  };

  const onPageChange = async (prop: number) => {
    setPaginationData((prev) => ({ ...prev, page: prop }));
    setProcess(true);
    await loadingList({ page: prop });
    setProcess(false);
  };
  const onPerPageChange = async (prop: number) => {
    setPaginationData((prev) => ({ ...prev, perPage: prop, page: 1 }));
    setProcess(true);
    await loadingList({ perPage: prop, page: 1 });
    setProcess(false);
  };

  const permission: any = [];
  const onAction = async ({ action, data }: any) => {
    if (action === "edit") {
      setActiveRecordData(data);
      onFormOpenChange(true);
    }
    if (action === "delete") {
      setActiveRecordData(data);
      setDeleteId(data.email_id);
      onDelete({
        description:
          "This action cannot be undone. This will permanently delete the record and remove its data from our system.",
        id: data.user_id,
        identity: "user",
        popupStatus: true,
        title: "Are you sure you want to delete this email listener?",
      });
      await loadingList({});
    }
  };

  useEffect(() => {
    if (formPopupOpen === false) {
      setActiveRecordData(undefined);
      closeErrorMessage();
    }
  }, [formPopupOpen]);

  const loadingList = async ({
    perPage,
    page,
    filterReset,
    makeFilter,
    searchText,
  }: {
    perPage?: number;
    page?: number;
    filterReset?: boolean;
    makeFilter?: boolean;
    searchText?: string;
  }) => {
    let apiFilter = {};

    if (!filterReset) {
      apiFilter = filter;
    }
    let page_no = page || paginationData.page;

    if (makeFilter) {
      page_no = 1;
    }

    const payload = {
      data: {
        filter_by: {
          ...apiFilter,
          search_text: searchText ?? searchDebounced,
          exact_match: false,
          pagination: {
            page_no: page_no,
            no_of_recs: perPage || paginationData.perPage,
            sort_by: "cts",
            order_by: false,
          },
        },
      },
      company_id: company_id,
      user_id: user_id,
    };
    const { data, error, message } = await apiServices<any>(
      API_SERVICE.get,
      "post",
      payload,
    );

    if (data) {
      setListData(data.result.metadata[API_SERVICE.key]);
      setPaginationData((prev) => ({
        ...prev,
        totalItem: data?.result?.metadata?.total_records || 1,
      }));
    }
    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }
    return { data, error };
  };

  const onFormSubmit = async (prop: any): Promise<any> => {
    let formData = {};
    if (activeRecordData) {
      formData = {
        email_id: prop.email_id,
        enable: prop.enable,
        read_body_content: prop.read_body_content,
        read_attachments: prop.read_attachments,
        settings: prop.settings,
        azure_app_settings: prop.azure_app_settings,
      };
    } else {
      formData = {
        is_new: true,
        email_id: prop.email_id,
        password: prop.password,
        enable: prop.enable,
        read_body_content: prop.read_body_content,
        read_attachments: prop.read_attachments,
        settings: prop.settings,
        azure_app_settings: prop.azure_app_settings,
      };
    }

    if (isFormReset) {
      formData = {
        email_id: activeRecordData.email_id,
        enable: activeRecordData.enable,
        read_body_content: activeRecordData.read_body_content,
        read_attachments: activeRecordData.read_attachments,
        settings: activeRecordData.settings,
        password: prop.password,
        azure_app_settings: activeRecordData.azure_app_settings,
      };
    }

    const payload = {
      data: formData,
      company_id: company_id,
      user_id: user_id,
    };

    const { data, error, message } = await apiServices<any>(
      API_SERVICE.post,
      "post",
      payload,
    );

    if (data) {
      const response = await loadingList({});
      if (response.data) {
        // toast({
        //   title: "Success",
        //   variant: "success",
        //   description: data.status.msg,
        // });
        toast({
          title: "Success",
          variant: "success",
          description:
            data.result.metadata.message ??
            data?.status?.msg ??
            data?.status?.message,
        });
      }
    }

    if (error) {
      setProcess(false);
    }

    if (error) {
      // toast({
      //   title: "Error",
      //   variant: "destructive",
      //   description: error.status?.message || message,
      // });
      setErrorMessage({
        title: "Error",
        message: error.status?.message || message,
        open: true,
      });
    }
    return { data, error };
  };

  const inlineUpdate = async (prop: any): Promise<any> => {
    console.log({ prop });
    setProcess(true);
    let formData: any = {
      email_id: prop.email_id,
      enable: prop.enable,
      read_body_content: prop.read_body_content,
      read_attachments: prop.read_attachments,
    };
    if (prop?.settings) {
      formData = {
        ...formData,
        settings: prop?.settings,
      };
    }
    const payload = {
      data: formData,
      company_id: company_id,
      user_id: user_id,
    };

    const { data, error, message } = await apiServices<any>(
      API_SERVICE.post,
      "post",
      payload,
    );

    if (data) {
      const response = await loadingList({});
      if (response.data) {
        // toast({
        //   title: "Success",
        //   variant: "success",
        //   description: data.status.msg,
        // });
        toast({
          title: "Success",
          variant: "success",
          description:
            data.result.metadata.message ??
            data?.status?.msg ??
            data?.status?.message,
        });
      }
      setProcess(false);
    }

    if (error) {
      setProcess(false);
    }

    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
      setProcess(false);
    }
    return { data, error };
  };

  const filterRequest = async () => {
    setProcess(true);
    await loadingList({ makeFilter: true });
    setProcess(false);
  };
  const clearFilter = async () => {
    setFilter({});
    setProcess(true);
    await loadingList({ filterReset: true });
    setProcess(false);
  };

  useEffect(() => {
    if (formPopupOpen === false) {
      setIsFormReset(false);
    }
  }, [formPopupOpen]);

  return {
    skeltonLoading,
    listData,
    onPageChange,
    onPerPageChange,
    paginationData,
    permission,
    onAction,
    activeRecordData,
    onFormSubmit,
    onFormOpenChange,
    formPopupOpen,
    tableColumn,
    setTableColumn,
    filter,
    setFilter,
    filterRequest,
    clearFilter,
    isFormReset,
    setIsFormReset,
    passKeyPopup,
    setPassKeyPopup,
    inlineUpdate,
    errorMessage,
    closeErrorMessage,
    emailProvider,
    setEmailProvider,
    aksPopup,
    setAksPopup,
  };
};
